/** A missing storage measurement is not evidence of zero stored water. */
type WaterService = 'municipalSupply' | 'wastewaterService';

function isZero(value: unknown): boolean {
  return value === 0 || (typeof value === 'string' && /^[+-]?0+(?:\.0+)?$/.test(value.trim()));
}

function serviceIn(text: string): WaterService | undefined {
  const wastewater = /\b(?:wastewater|sewer|sewage|effluent)\b/i.test(text);
  const supply = /\b(?:water|potable|drinking)\b/i.test(text);
  return wastewater === supply ? undefined : wastewater ? 'wastewaterService' : 'municipalSupply';
}

/** Require an affirmative, service-specific zero volume or absence of storage.
 * A daily flow, unknown value, future plan or missing information cannot witness
 * storage. The bounded heading context supports ordinary utility tables. */
function hasZeroStorageWitness(body: string, service: WaterService): boolean {
  let headingService: WaterService | undefined;
  for (const clause of body.split(/[\r\n]+|;\s*|(?<=[.!?])\s+/)) {
    const heading = clause.trim().replace(/:$/, '');
    if (/^(?:(?:municipal|potable|drinking)\s+)?water(?:\s+(?:supply|system|service))?$|^(?:wastewater|sewer|sewage)(?:\s+(?:system|service))?$/i.test(heading)) {
      headingService = serviceIn(heading);
      continue;
    }
    if (!/\bstorage\b/i.test(clause)) {
      if (clause.length <= 160 && /\b(?:water|wastewater|sewer)\s+(?:provider|system|supply|service)\b/i.test(clause)) headingService = serviceIn(clause);
      continue;
    }
    const statedService = serviceIn(clause);
    // Mixed-service clauses need separate statements rather than assigning a
    // water zero to sewer storage (or vice versa).
    if ((/\b(?:wastewater|sewer|sewage|effluent)\b/i.test(clause)
      && /\b(?:water|potable|drinking)\b/i.test(clause))
      || (statedService ?? headingService) !== service) continue;
    if (/\b(?:unknown|unverified|unconfirmed|undetermined|unspecified|unavailable|assumed|estimated|planned|proposed|required|requirements|needed|may|might|could|would|will|if)\b|\bnot\s+(?:known|provided|stated|reported|specified|available)\b|\bno\b[^.!?;]{0,60}\bstorage\b[^.!?;]{0,40}\b(?:provided|stated|reported|specified|documented|recorded|information|data|details|figures?|quantit(?:y|ies))\b/i.test(clause)) continue;
    const zeroVolume = '(?<![\\d.])(?:0(?:\\.0+)?|zero)\\s*(?:US\\s+)?(?:gallons?|gals?)\\b(?!\\s*(?:per|/)\\s*(?:day|hour|minute))';
    if (new RegExp(`\\bstorage\\b[^;.!?\\n]{0,40}\\b${zeroVolume}|\\b${zeroVolume}[^;.!?\\n]{0,40}\\bstorage\\b`, 'i').test(clause)
      || /\bstorage\s*(?:\(\s*(?:US\s+)?gallons?\s*\)|(?:in\s+)?gallons?)\s*[:=]?\s*0(?:\.0+)?\b/i.test(clause)
      || /\b(?:no|zero)\s+(?:(?:on[- ]site|existing|municipal|potable|drinking|water|wastewater|sewer|sewage)\s+)*storage\b/i.test(clause)) return true;
  }
  return false;
}

/** Shared by fresh section normalization and every cached section read. */
export function filterWaterStorageZeros(data: Record<string, unknown>, body: string): void {
  for (const service of ['municipalSupply', 'wastewaterService'] as const) {
    const raw = data[service];
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const fields = raw as Record<string, unknown>;
    if (isZero(fields.storageGallons) && !hasZeroStorageWitness(body, service)) {
      delete fields.storageGallons;
      // A cache hit must not survive solely as provenance for a rejected zero.
      if (!Object.keys(fields).some(key => key !== 'provenance' && key !== 'notes')) delete data[service];
    }
  }
}

/** The requirement cache and Populate use flat authored-schema keys. */
export function filterWaterStorageRequirementZeros(fields: Record<string, unknown>, body: string): void {
  if (isZero(fields.supplyStorageGallons) && !hasZeroStorageWitness(body, 'municipalSupply')) delete fields.supplyStorageGallons;
}
